import React, { useState } from "react";
import { submitBooking } from "@/lib/api";

const TIERS = [
  { key: "digital",  label: "Digital (Virtual) Appearance", range: "$5,000 – $10,000", inc: "30-min Zoom/Stream. No travel." },
  { key: "regional", label: "Regional In-Person Appearance", range: "$15,000 – $25,000", inc: "2-hour local event. No overnight stay." },
  { key: "national", label: "National In-Person Appearance", range: "$30,000+",          inc: "4-hour event + overnight travel costs." },
];

/**
 * Sean's Booking Agreement — GATED FLOW
 *  Stage 1: lead capture (org, name, email, phone)
 *  Stage 2: 6-digit code sent to email → user enters it
 *  Stage 3 (post-verify): contract + full booking form unlocked
 *  Only serious, verified contacts ever see the agreement details.
 */
export default function BookingSection() {
  const [open, setOpen] = useState(false);
  return (
    <section
      id="bookings"
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 mt-12 mb-8"
      data-testid="bookings-section"
    >
      <div className="card tint-cream" style={{ padding: 0, overflow: "hidden" }}>
        <button
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="w-full text-left flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 px-4 sm:px-6 py-4 sm:py-5"
          data-testid="bookings-toggle"
          style={{ background: "transparent" }}
        >
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <div className="text-2xl sm:text-3xl shrink-0" aria-hidden>📜</div>
            <div className="min-w-0 flex-1">
              <div className="eyebrow text-[var(--orange)]">official · verified-only</div>
              <div className="font-display text-lg sm:text-2xl text-[var(--ink)] leading-tight">
                Book Sean for Your Event
              </div>
              <div className="text-xs sm:text-sm text-[var(--ink-soft)] mt-0.5">
                Verify your email to unlock the Master Service Agreement &amp; application
              </div>
            </div>
          </div>
          <div
            className="self-start sm:self-center shrink-0 pill"
            style={{ background: open ? "var(--orange)" : "var(--navy)", color: "#fff", padding: "0.4rem 0.85rem" }}
            data-testid="bookings-chevron"
          >
            {open ? "Hide ▴" : "Start ▾"}
          </div>
        </button>

        {open && (
          <div className="px-4 sm:px-6 pb-6" data-testid="bookings-body">
            <BookingGate />
          </div>
        )}
      </div>
    </section>
  );
}

/* ============= GATE STATE MACHINE ============= */
function BookingGate() {
  const [stage, setStage] = useState("lead"); // lead | contract | done
  const [lead, setLead] = useState({ org_name: "", contact_name: "", contact_email: "", contact_phone: "" });
  const [submittedBooking, setSubmittedBooking] = useState(null);

  if (stage === "done") {
    return (
      <div className="card-flat tint-green" data-testid="bookings-success">
        <div className="font-display text-xl text-[var(--ink)] mb-1">✅ Application received!</div>
        <p className="text-sm text-[var(--ink)]">
          Reference&nbsp;
          <code className="font-mono bg-white px-1.5 py-0.5 rounded border border-[var(--ink)] text-xs">
            {submittedBooking?.id?.slice(0, 8)}
          </code>{" "}
          — Sean's Team will review and reach out to <strong>{submittedBooking?.contact_email}</strong>{" "}
          (or call <strong>{submittedBooking?.contact_phone}</strong>) within 5 business days. Keep on sciencing! 🧡
        </p>
      </div>
    );
  }

  return (
    <>
      <StageNav stage={stage} />
      {stage === "lead" && (
        <LeadCaptureForm
          initial={lead}
          onSubmitted={(updatedLead) => { setLead(updatedLead); setStage("contract"); }}
        />
      )}
      {stage === "contract" && (
        <>
          <ContractText />
          <BookingForm
            prefill={lead}
            onSubmitted={(b) => { setSubmittedBooking(b); setStage("done"); }}
          />
        </>
      )}
    </>
  );
}

function StageNav({ stage }) {
  const stages = [
    { key: "lead",     label: "1 · Contact" },
    { key: "contract", label: "2 · Agree" },
  ];
  return (
    <div className="flex gap-1.5 mb-4" data-testid="gate-stage-nav">
      {stages.map((s) => {
        const active = s.key === stage;
        return (
          <div
            key={s.key}
            data-testid={`stage-${s.key}${active ? "-active" : ""}`}
            className="flex-1 text-center pill"
            style={{
              background: active ? "var(--orange)" : "var(--paper-2)",
              color: active ? "#fff" : "var(--ink)",
              fontSize: "0.66rem",
              padding: "0.35rem 0.6rem",
              letterSpacing: "0.04em",
            }}
          >
            {s.label}
          </div>
        );
      })}
    </div>
  );
}

/* ============= STAGE 1: LEAD CAPTURE ============= */
function LeadCaptureForm({ initial, onSubmitted }) {
  const [f, setF] = useState(initial);
  const [err, setErr] = useState("");
  const update = (k) => (e) => setF((prev) => ({ ...prev, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    setErr("");
    if (!f.org_name || !f.contact_name) return setErr("Please enter your organization and your name.");
    if (!/^\S+@\S+\.\S+$/.test(f.contact_email)) return setErr("Please enter a valid email.");
    if (!f.contact_phone || f.contact_phone.replace(/\D/g, "").length < 7) return setErr("Please enter a valid phone number.");
    onSubmitted(f);
  };

  return (
    <form onSubmit={submit} className="card-flat bg-white" data-testid="lead-capture-form">
      <div className="font-display text-lg text-[var(--ink)] mb-1">Tell us about your event</div>
      <p className="text-xs text-[var(--ink-soft)] mb-4">
        Sean's Team reviews every booking personally. Share your contact details below, agree to the Master Service Agreement on the next screen, and we'll reach out within 5 business days.
      </p>

      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Organization / School *">
          <input value={f.org_name} onChange={update("org_name")} className="field" data-testid="gate-org" required />
        </Field>
        <Field label="Your Name *">
          <input value={f.contact_name} onChange={update("contact_name")} className="field" data-testid="gate-name" required />
        </Field>
        <Field label="Email *">
          <input type="email" value={f.contact_email} onChange={update("contact_email")} className="field" data-testid="gate-email" required autoComplete="email" />
        </Field>
        <Field label="Phone *">
          <input type="tel" value={f.contact_phone} onChange={update("contact_phone")} className="field" data-testid="gate-phone" required autoComplete="tel" />
        </Field>
      </div>

      {err && <div className="mt-3 text-sm font-bold text-[var(--red)]" data-testid="gate-error">{err}</div>}

      <div className="mt-4 flex flex-col sm:flex-row gap-2">
        <button type="submit" className="btn btn-red w-full sm:w-auto" data-testid="gate-send-code">
          Continue to contract →
        </button>
      </div>
      <div className="mt-3 text-[0.7rem] font-mono text-[var(--ink-soft)]">
        🔒 Your contact info is stored privately and only seen by Sean's Team.
      </div>
    </form>
  );
}

/* ============= STAGE 3 PART A: CONTRACT TEXT ============= */
function ContractText() {
  return (
    <div
      className="card-flat bg-white mt-1 mb-5"
      style={{ maxHeight: 360, overflowY: "auto", padding: "1.1rem 1.2rem", lineHeight: 1.55 }}
      data-testid="contract-body"
    >
      <h3 className="font-display text-xl text-[var(--ink)] mb-1">
        The "Sean The Science Kid" Standard Booking Agreement
      </h3>
      <div className="label-tag mb-3">Nationwide Talent &amp; Digital Appearance Contract</div>
      <p className="text-sm text-[var(--ink)] mb-3">
        <strong>Welcome to the Lab!</strong> By selecting "I Agree" and completing the variables in the app, you agree to the terms set forth in this Master Service Agreement.
      </p>

      <Clause num="1" title="Parties &amp; Definitions">
        <li><strong>The Talent:</strong> Sean Atitsogbe ("Sean The Science Kid"), represented by his Parent/Guardian ("Guardian").</li>
        <li><strong>The Engager:</strong> The entity or individual completing the booking request via the App ("Client").</li>
        <li><strong>The Event:</strong> The specific virtual or in-person appearance defined in the variables below.</li>
      </Clause>

      <Clause num="2" title="Scope of Services (Standardized)">
        <p className="mb-1">Sean agrees to appear as the "Principal Investigator." Services are strictly limited to:</p>
        <li><strong>A. The Segment (20–30 min):</strong> A science discussion, reading of a short passage, or "Neurocardio Loop" explanation.</li>
        <li><strong>B. The Q&amp;A (5–10 min):</strong> Moderated questions (Client provides questions 48 hrs in advance).</li>
        <li><strong>C. The Honorary Ceremony:</strong> Presentation of pre-purchased awards/books.</li>
      </Clause>

      <Clause num="3" title="Nationwide Compensation &amp; Fees">
        <p className="mb-2 text-xs italic text-[var(--ink-soft)]">Fees are standardized nationwide; travel is not included for in-person events.</p>
        <div className="overflow-x-auto -mx-2">
          <table className="w-full text-xs sm:text-sm" style={{ borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "var(--paper-2)" }}>
                <th className="text-left p-2 border border-[var(--ink)]">Booking Tier</th>
                <th className="text-left p-2 border border-[var(--ink)]">Price (USD)</th>
                <th className="text-left p-2 border border-[var(--ink)]">Inclusions</th>
              </tr>
            </thead>
            <tbody>
              {TIERS.map((t) => (
                <tr key={t.key}>
                  <td className="p-2 border border-[var(--ink)] font-bold">{t.label}</td>
                  <td className="p-2 border border-[var(--ink)] font-mono whitespace-nowrap">{t.range}</td>
                  <td className="p-2 border border-[var(--ink)]">{t.inc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-2 text-sm"><strong>Payment Schedule:</strong></p>
        <li><strong>50% Deposit:</strong> Due immediately upon signing to lock the date. Non-refundable.</li>
        <li><strong>50% Balance:</strong> Due 14 days prior to the Event Date.</li>
      </Clause>

      <Clause num="4" title="The Standard Rider (Nationwide Logistics)">
        <p className="mb-1 text-sm"><strong>A. Travel &amp; Accommodation (in-person only):</strong></p>
        <li><strong>Flights:</strong> Round-trip, direct when possible, Economy Plus or equivalent for Talent and one (1) Guardian.</li>
        <li><strong>Hotel:</strong> One (1) standard room at a 3-star+ national chain.</li>
        <li><strong>Ground Transport:</strong> Ride-share credits or a private car for airport/hotel/venue transfers.</li>
        <p className="mt-2 text-sm"><strong>B. Hospitality ("The Green Room"):</strong></p>
        <li>Clean, quiet room backstage.</li>
        <li>Bottled water (room temp) and a light snack. <strong>No alcohol</strong> on site.</li>
      </Clause>

      <Clause num="5" title="Legal Protocols (Non-Negotiable)">
        <li><strong>Minor Supervision:</strong> The Guardian must be present within sightline at all times.</li>
        <li><strong>Photo/Video:</strong> Personal/marketing use of this specific event only. Selling footage requires separate licensing.</li>
        <li><strong>Morality Clause:</strong> Sean reserves the right to pause/cancel unsafe or adult-content venues with full fee retained.</li>
      </Clause>

      <Clause num="6" title="Cancellation Policy">
        <div className="overflow-x-auto -mx-2">
          <table className="w-full text-xs sm:text-sm" style={{ borderCollapse: "collapse" }}>
            <thead><tr style={{ background: "var(--paper-2)" }}>
              <th className="text-left p-2 border border-[var(--ink)]">Cancellation Window</th>
              <th className="text-left p-2 border border-[var(--ink)]">Refund Terms</th>
            </tr></thead>
            <tbody>
              <tr><td className="p-2 border border-[var(--ink)]">More than 60 days out</td><td className="p-2 border border-[var(--ink)]">Deposit retained (applies to future date within 1 year).</td></tr>
              <tr><td className="p-2 border border-[var(--ink)]">30 – 59 days out</td><td className="p-2 border border-[var(--ink)]">50% of deposit refunded.</td></tr>
              <tr><td className="p-2 border border-[var(--ink)]">Less than 14 days out</td><td className="p-2 border border-[var(--ink)]">No refund; full balance due.</td></tr>
            </tbody>
          </table>
        </div>
      </Clause>

      <Clause num="7" title="Force Majeure">
        <p className="text-sm">Acts of God, natural disaster, or government mandates canceling travel nationwide void the contract with a full refund of deposit.</p>
      </Clause>

      <Clause num="8" title="Governing Law">
        <p className="text-sm">Governed by the laws of the <strong>State of Georgia</strong> for nationwide uniformity.</p>
      </Clause>
    </div>
  );
}

function Clause({ num, title, children }) {
  return (
    <div className="mb-3 pb-2" style={{ borderBottom: "1px dashed var(--ink-soft)" }}>
      <div className="font-display text-base text-[var(--ink)] mb-1">{num}. <span dangerouslySetInnerHTML={{ __html: title }} /></div>
      <ul className="space-y-1 pl-4 text-sm text-[var(--ink)]" style={{ listStyle: "disc" }}>{children}</ul>
    </div>
  );
}

/* ============= STAGE 2 PART B: BOOKING FORM ============= */
function BookingForm({ prefill, onSubmitted }) {
  const [f, setF] = useState({
    org_name: prefill.org_name || "",
    contact_name: prefill.contact_name || "",
    contact_email: prefill.contact_email || "",
    contact_phone: prefill.contact_phone || "",
    tier: "digital",
    event_date: "",
    event_city: "",
    event_state: "",
    audience_size: "",
    description: "",
    agreed: false,
    website: "", // honeypot — humans never fill this; bots auto-fill
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const update = (k) => (e) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setF((prev) => ({ ...prev, [k]: val }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!f.agreed) return setErr("Please check 'I Agree' to the Master Service Agreement to submit.");
    setSubmitting(true);
    try {
      const body = { ...f, audience_size: Number(f.audience_size) || 0 };
      const res = await submitBooking(body);
      onSubmitted(res);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Couldn't submit — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="card-flat bg-white" data-testid="bookings-form">
      {/* Honeypot — hidden via CSS, off-screen, not tabbable. Bots WILL fill it. */}
      <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", top: "auto", width: "1px", height: "1px", overflow: "hidden" }}>
        <label htmlFor="website-hp">Website (leave blank)</label>
        <input
          id="website-hp"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={f.website}
          onChange={update("website")}
        />
      </div>
      <div className="flex items-center gap-2 mb-1">
        <div className="font-display text-lg text-[var(--ink)]">Booking Application</div>
      </div>
      <p className="text-xs text-[var(--ink-soft)] mb-4">
        Complete the variables — these become Schedule&nbsp;A of your contract upon Sean's Team approval.
      </p>

      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Organization / School *"><input value={f.org_name} onChange={update("org_name")} className="field" data-testid="book-org" required /></Field>
        <Field label="Contact Name *"><input value={f.contact_name} onChange={update("contact_name")} className="field" data-testid="book-contact" required /></Field>
        <Field label="Contact Email *"><input type="email" value={f.contact_email} onChange={update("contact_email")} className="field" data-testid="book-email" required readOnly /></Field>
        <Field label="Phone *"><input value={f.contact_phone} onChange={update("contact_phone")} className="field" data-testid="book-phone" /></Field>

        <Field label="Booking Tier *">
          <select value={f.tier} onChange={update("tier")} className="field" data-testid="book-tier">
            {TIERS.map((t) => <option key={t.key} value={t.key}>{t.label} — {t.range}</option>)}
          </select>
        </Field>
        <Field label="Event Date"><input type="date" value={f.event_date} onChange={update("event_date")} className="field" data-testid="book-date" /></Field>

        <Field label="Event City"><input value={f.event_city} onChange={update("event_city")} className="field" data-testid="book-city" placeholder="Atlanta" /></Field>
        <Field label="State"><input value={f.event_state} onChange={update("event_state")} className="field" data-testid="book-state" placeholder="GA" /></Field>

        <Field label="Expected Audience Size"><input type="number" min={0} value={f.audience_size} onChange={update("audience_size")} className="field font-mono" data-testid="book-audience" placeholder="250" /></Field>
        <div />

        <div className="sm:col-span-2">
          <Field label="Event Description">
            <textarea rows={3} value={f.description} onChange={update("description")} className="field" placeholder="Briefly describe the audience, theme, and what you'd like Sean to cover." data-testid="book-desc" />
          </Field>
        </div>
      </div>

      <label className="flex items-start gap-2 mt-4 cursor-pointer" data-testid="book-agree-label">
        <input type="checkbox" checked={f.agreed} onChange={update("agreed")} className="mt-1" data-testid="book-agree" />
        <span className="text-sm text-[var(--ink)]">
          <strong>I Agree.</strong> By submitting this application I agree to the terms of the Master Service Agreement above, including fees, payment schedule, rider, cancellation policy, and Governing Law (State of Georgia).
        </span>
      </label>

      {err && <div className="mt-3 text-sm font-bold text-[var(--red)]" data-testid="book-error">{err}</div>}

      <div className="flex flex-col sm:flex-row gap-2 mt-4">
        <button type="submit" disabled={submitting} className="btn btn-red w-full sm:w-auto" data-testid="book-submit">
          {submitting ? "Submitting…" : "📩 Submit Application"}
        </button>
        <a href="#top" className="btn-ghost w-full sm:w-auto text-center">Back to top ↑</a>
      </div>
    </form>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="label-tag mb-1">{label}</div>
      {children}
    </label>
  );
}
